import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Paper
} from "@mui/material";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import type { QuantityConfirmModalProps } from "../interfaces/modals";

export type { QuantityConfirmModalProps };

const QuantityConfirmModal: React.FC<QuantityConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  productName,
  remainingQuantity,
  unit = "units",
  isLoading = false
}) => {
  const { t } = useKivunovaTranslation();
  const [quantity, setQuantity] = useState<number>(1);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (open) {
      setQuantity(1);
      setError("");
    }
  }, [open]);

  const handleConfirm = () => {
    if (quantity <= 0) {
      setError(t("quantity-confirm-min-error", "Quantity must be at least 1"));
      return;
    }
    if (quantity > remainingQuantity) {
      setError(
        t(
          "quantity-confirm-max-available",
          "Maximum {{remainingQuantity}} {{unit}} available",
          { remainingQuantity, unit }
        )
      );
      return;
    }
    setError("");
    onConfirm(quantity);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 2
        }}
      >
        <Paper
          sx={{
            p: 3,
            maxWidth: 400,
            width: "100%",
            outline: "none"
          }}
          onKeyDown={handleKeyDown}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {productName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("quantity-confirm-remaining", "Remaining")}: {remainingQuantity}{" "}
            {unit}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={t("quantity-confirm-enter-label", "Enter quantity")}
            type="number"
            value={quantity}
            onChange={(e) =>
              setQuantity(Math.max(0, parseInt(e.target.value, 10) || 0))
            }
            inputProps={{ min: 1, max: remainingQuantity }}
            error={!!error}
            helperText={error}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button onClick={onClose} disabled={isLoading}>
              {t("quantity-confirm-cancel", "Cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isLoading}
              sx={{
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {isLoading
                ? t("quantity-confirm-confirming", "Confirming…")
                : t("quantity-confirm-confirm", "Confirm")}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default QuantityConfirmModal;
